/* eslint-disable no-await-in-loop */
import React, { useState, useEffect } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { Stack, Box, TextField, Typography, Switch, Divider, Tooltip } from '@mui/material';
import { Upload } from 'antd';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as generateUUID } from 'uuid';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import axiosInstance from '../../utils/axiosInstance';
import SingleSelector from '../single-selector/singleSelector';
import fetchDeviceList from '../../utils/fetchDeviceList';
import fetchStreamList from '../../utils/fetchStreamList';
import { appSliceActions } from '../../appSlices/appSlice';

const { Dragger } = Upload;
const VIDEO_UPLOAD_TIMEOUT = 999999999;

const toolTipHelperText = "If enabled the media file will be divided into small chunks and each chunk will be uploaded separately. Enable this setting if timeout is occuring during upload."

function hasWhiteSpace(s) {
  return /\s/g.test(s);
}

const FileUpload = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { adaptorType } = window;
  const hostList = useSelector((state) => state.app.hostList);
  const dispatch = useDispatch();
  const [vstHost, setVstHost] = useState(null);
  const [enableTranscode, setEnableTranscode] = useState(false);
  const [frameRate, setFrameRate] = useState('');
  const [bitrate, setBitrate] = useState('');
  const [tags, setTags] = useState('');
  const [enableChunkUpload, setEnableChunkUpload] = useState(false);
  const [chunkSize, setChunkSize] = useState(50);

  useEffect(() => {
    if (hostList && hostList.length === 1) {
      setVstHost(hostList[0]);
    }
  }, [hostList]);

  const handleRefresh = async () => {
    hostList.forEach(async (host) => {
      const sensorList = await fetchDeviceList(host);
      if (sensorList != null) {
        for (let i = 0; i < sensorList.length; i += 1) {
          sensorList[i].host = host;
        }
        dispatch(appSliceActions.addDevice(sensorList));
      }
      const streamList = await fetchStreamList(host);
      if (streamList != null) {
        for (let i = 0; i < streamList.length; i += 1) {
          streamList[i].host = host;
        }
        dispatch(appSliceActions.addStream(streamList));
      }
    });
  };

  const handleEnableTranscode = () => {
    setEnableTranscode(!enableTranscode);
  };

  const setFileTag = (fileTags, deviceId) => {
    const jsonData = {
      tags: fileTags,
    };
    axiosInstance
      .post(`${window.location.protocol}//${vstHost}/api/v1/sensor/${deviceId}/info`, jsonData)
      .then((res) => {
        console.log('set tag response: ', res.data);
      })
      .catch((e) => {
        console.debug('failed to set tags', e);
      });
  };

  const customFileUpload = (options) => {
    if (!vstHost) {
      enqueueSnackbar(`Error - ${adaptorType} device not selected`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      return;
    }
    if (!enableChunkUpload) {
      uploadFileAsSingleChunk(options);
    }
    else {
      uploadFileInChunks(options);
    }
  };

  const isNumeric = (value) => /^-?\d+$/.test(value)


  const uploadFileInChunks = async (options) => {
    const handleSuccess = (response, chunkNumber, totalChunkCount, onSuccess) => {
      enqueueSnackbar(`Success - File upload successfully ${response.data.filename}`, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      console.log('file upload response', response.data);
      onSuccess('Ok');
      if (chunkNumber === totalChunkCount) {
        handleRefresh();
      }
    }

    const handleUploadError = (error, chunkNumber, cancelToken, onError) => {
      cancelToken.cancel();
      console.error(`Error uploading chunk ${chunkNumber}:`, error);
      onError({ error });
      enqueueSnackbar(`Error - File upload failed`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
    }

    const finalizeUpload = (onSuccess, file) => {
      onSuccess('Ok');
      enqueueSnackbar(`Success - All chunks uploaded successfully for ${file.name}`, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
    }
    const { onSuccess, onError, file, onProgress } = options;
    if (!isNumeric(chunkSize)) {
      enqueueSnackbar(`Error - chunkSize must be a valid number`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      onError({});
      return;
    }
    if (Number(chunkSize) <= 10) {
      enqueueSnackbar('Error - chunkSize must be more than 10 MB', {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      onError({});
      return;
    }
    let chunkFailed = false;
    let chunkNumber = 0;
    const chunkSizeBytes = Number(chunkSize) * 1000 * 1000; // Convert MB to Bytes
    const totalChunkCount = Math.ceil(file.size / chunkSizeBytes);
    const cancelVideoUpload = axios.CancelToken.source();
    const uniqueIdentifier = generateUUID();
    const fileSize = file.size;

    const baseHeaders = {
      'content-type': 'multipart/form-data',
      'nvstreamer-identifier': uniqueIdentifier,
      'nvstreamer-file-name': file.name
    };

    for (let start = 0; start < fileSize; start += chunkSizeBytes) {
      if (chunkFailed) break;
      chunkNumber += 1;
      const chunk = file.slice(start, start + chunkSizeBytes);
      const fd = new FormData();
      fd.append('file', chunk);
      fd.append('filename', file.name);

      const uploadHeaders = {
        ...baseHeaders,
        'nvstreamer-chunk-number': chunkNumber,
        'nvstreamer-total-chunks': totalChunkCount,
        'nvstreamer-is-last-chunk': totalChunkCount === chunkNumber ? "true" : "false",
      };

      try {
        const response = await axiosInstance.post(`${window.location.protocol}//${vstHost}/api/v1/storage/file`, fd, {
          timeout: VIDEO_UPLOAD_TIMEOUT,
          headers: uploadHeaders,
          cancelToken: cancelVideoUpload.token,
          onUploadProgress: (progressEvent) => {
            const progress = (start + progressEvent.loaded) / fileSize * 100;
            onProgress({ percent: progress });
          }
        });

        if (response.data && Object.prototype.hasOwnProperty.call(response.data, 'filename')) {
          setFileTag(tags, response.data.id);
          handleSuccess(response, chunkNumber, totalChunkCount, onSuccess);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          handleUploadError(error, chunkNumber, cancelVideoUpload, onError);
          chunkFailed = true;
          chunkNumber -= 1;
        }
      }
    }

    if (chunkNumber === totalChunkCount) {
      finalizeUpload(onSuccess, file);
    }
  };

  const uploadFileAsSingleChunk = (options) => {
    const setupUploadHeaders = (file) => {
      const headers = {
        'content-type': 'multipart/form-data',
        'nvstreamer-chunk-number': 1,
        'nvstreamer-total-chunks': 1,
        'nvstreamer-is-last-chunk': true,
        'nvstreamer-identifier': generateUUID(),
        'nvstreamer-file-name': file.name,
      };

      if (enableTranscode) {
        console.log('adding transcode header');
        headers['nvstreamer-enable-transcode'] = true;
        if (frameRate) {
          console.log('adding framerate header');
          headers['nvstreamer-transcode-framerate'] = frameRate;
        }
        if (bitrate) {
          console.log('adding bitrate header');
          headers['nvstreamer-transcode-bitrate'] = bitrate;
        }
      }
      console.log('using upload headers', headers);
      return headers;
    }

    const handleResponse = (response, onSuccess) => {
      if (response.data && Object.prototype.hasOwnProperty.call(response.data, 'filename')) {
        console.log('file upload response', response.data);
        onSuccess('Ok');
        enqueueSnackbar(`Success - File upload successfully ${response.data.filename}`, {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        handleRefresh();
      }
    }

    const handleError = (error, onError) => {
      console.error('File upload error', error);
      onError({ error });
      enqueueSnackbar(`Error - ${error.response.data.error_message}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
    }
    const { onSuccess, onError, file, onProgress } = options;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('filename', file.name);

    console.log(file.size);
    console.log(file);
    console.log('transcode ?', enableTranscode);

    const uploadHeaders = setupUploadHeaders(file);

    axiosInstance.post(`${window.location.protocol}//${vstHost}/api/v1/storage/file`, fd, {
      timeout: VIDEO_UPLOAD_TIMEOUT,
      headers: uploadHeaders,
      onUploadProgress: (event) => {
        onProgress({ percent: (event.loaded / event.total) * 100 });
      },
    })
      .then((response) => { setFileTag(tags, response.data.id); handleResponse(response, onSuccess); })
      .catch((error) => handleError(error, onError));
  };

  const props = {
    multiple: true,
    customRequest: customFileUpload,
    onRemove: (file) => {
      console.log('Remove clicked', file);
    },
    // eslint-disable-next-line consistent-return
    beforeUpload: (file) => {
      if (hasWhiteSpace(file.name)) {
        enqueueSnackbar(`Error - whitespaces not allowed in file name`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        return false;
      }
      if (!vstHost) {
        enqueueSnackbar(`Error - please select ${adaptorType} device`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        return false;
      }
      return true;
    },
  };

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <SingleSelector
          id="remove-vst"
          placeholder="Type to search"
          label={`Select ${adaptorType} device*`}
          value={vstHost}
          onChange={setVstHost}
          getOptionLabel={(option) => `${adaptorType} at ${option}`}
          options={hostList}
        />
        <TextField
          fullWidth
          label="Tags"
          value={tags}
          onChange={(e) => {
            setTags(e.target.value);
          }}
          placeholder="tag1, tag2... tagn"
        />
        <Divider sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Transcode options
          </Typography>
        </Divider>
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle2">Transcode:</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1 }}>
            <Switch checked={enableTranscode} onChange={handleEnableTranscode} />
          </Stack>
        </Stack>
        <TextField
          fullWidth
          label="Transcode framerate"
          value={frameRate}
          onChange={(e) => {
            setFrameRate(e.target.value);
          }}
          placeholder=""
          disabled={!enableTranscode}
        />
        <TextField
          fullWidth
          label="Transcode bitrate"
          value={bitrate}
          onChange={(e) => {
            setBitrate(e.target.value);
          }}
          placeholder=""
          disabled={!enableTranscode}
        />
      </Stack>
      <Divider sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Chunk upload
        </Typography>
      </Divider>

      <Stack direction="row" alignItems="center" >
        <Typography variant="subtitle2">Enable multi-part upload:</Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1 }}>
          <Switch checked={enableChunkUpload} onChange={() => { setEnableChunkUpload((prev) => !prev) }} />
          <Tooltip title={toolTipHelperText}>
            <HelpOutlineIcon />
          </Tooltip>
        </Stack>
      </Stack>


      <TextField
        fullWidth
        label="Chunk size in MB"
        value={chunkSize}
        onChange={(e) => {
          setChunkSize(e.target.value);
        }}
        placeholder=""
        disabled={!enableChunkUpload}
        sx={{ mt: 1 }}
      />

      <Divider sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Upload files
        </Typography>
      </Divider>
      <Dragger {...props} disabled={!vstHost}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {vstHost
            ? `Click or drag file to this area to upload`
            : `Please select ${adaptorType} device before uploading files`}
        </p>
        <p className="ant-upload-hint">Single or bulk upload supported. Provide tags before uploading files. </p>
      </Dragger>
    </Box>
  );
};
export default FileUpload;
